import { FC, useState } from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, isSameMonth, isWeekend, startOfDay, startOfMonth } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  ExitSlipCascadeMove,
  ExitSlipPastTag,
  lessonTag,
  shortDay,
} from '../../businessLogicService/exitSlipSnapshotHelper';
import {
  exitSlipCanceledDot,
  exitSlipDateText,
  exitSlipDayBox,
  exitSlipDayHome,
  exitSlipDayShape,
  exitSlipTagSlot,
  exitSlipTagText,
} from './exitSlipCalendarTokens';

interface Props {
  //only the day and the title are read here, so any tagged-day list drives the grid
  schedule: ExitSlipPastTag[];
  pastTags: ExitSlipPastTag[];
  //carries each blocked day's lesson title: a greyed, untagged cell reads as
  //"unavailable for no reason"
  blocked: ExitSlipPastTag[];
  target: Date | null;
  onPick: (day: Date) => void;
  //reschedule-only: the side panel picks a free day and never cascades, so it omits these
  movingIndex?: number;
  plan?: (day: Date) => ExitSlipCascadeMove[];
  //the panel drops canceled tickets from its claims, so only the reschedule grid marks them
  canceled?: Date[];
  //the dashboard picker browses results that have already happened, so for it a past day is a
  //legitimate pick and the grid must open on whichever month the current one sits in
  selectablePast?: boolean;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const key = (date: Date) => startOfDay(date).getTime();

const navClass =
  'tw-flex tw-size-8 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-transparent hover:tw-bg-black/[0.04] disabled:tw-invisible';

const ExitSlipCalendar: FC<Props> = ({
  schedule,
  pastTags,
  blocked,
  canceled = [],
  movingIndex = -1,
  target,
  plan,
  onPick,
  selectablePast = false,
}) => {
  const today = startOfDay(new Date());
  const [month, setMonth] = useState(startOfMonth(target ?? today));
  const [hover, setHover] = useState<Date | null>(null);

  const mover = schedule[movingIndex];
  //with no mover there is no day to vacate, so nothing is excluded from the grid — the panel
  //picker has no lesson in flight and must keep today selectable
  const source = mover?.date ?? null;
  const preview = hover ?? target;

  const pastByDay = new Map(pastTags.map(el => [key(el.date), el.title]));
  const blockedByDay = new Map(blocked.map(el => [key(el.date), el.title]));
  const canceledDays = new Set(canceled.map(key));

  const occupancy = new Map(schedule.map(row => [key(row.date), { title: row.title, moved: false }]));

  if (preview && plan) {
    const moves = plan(preview);
    if (source) occupancy.delete(key(source));
    moves.forEach(move => occupancy.delete(key(move.from)));
    moves.forEach(move =>
      occupancy.set(key(move.to), {
        title: schedule[move.index]?.title ?? '',
        moved: true,
      }),
    );
    occupancy.set(key(preview), { title: mover?.title ?? '', moved: false });
  }

  const days = eachDayOfInterval({ start: month, end: endOfMonth(month) });

  return (
    <div className="tw-flex tw-w-[296px] tw-shrink-0 tw-flex-col tw-gap-2 tw-rounded-[14px] tw-border-2 tw-border-b-4 tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-3">
      <div className="tw-flex tw-items-center tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-[#F1F0EE] tw-pb-2">
        <button
          type="button"
          aria-label="Previous month"
          disabled={!selectablePast && isSameMonth(month, today)}
          onClick={() => setMonth(prev => addMonths(prev, -1))}
          className={navClass}
        >
          <ChevronLeft size={18} strokeWidth={2} className="tw-text-neutral-950" />
        </button>
        <span className="tw-grow tw-text-center tw-text-base tw-font-bold tw-leading-5 tw-text-neutral-950">
          {month.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setMonth(prev => addMonths(prev, 1))}
          className={navClass}
        >
          <ChevronRight size={18} strokeWidth={2} className="tw-text-neutral-950" />
        </button>
      </div>

      <div className="tw-grid tw-grid-cols-7 tw-gap-0.5">
        {WEEKDAYS.map((label, index) => (
          <span key={index} className="tw-text-center tw-text-xs tw-font-bold tw-leading-5 tw-text-[#686868]">
            {label}
          </span>
        ))}
      </div>

      <div className="tw-grid tw-grid-cols-7 tw-gap-x-0.5 tw-gap-y-1" onMouseLeave={() => setHover(null)}>
        {days.map(date => {
          const dayKey = key(date);
          const past = date < today;
          const readOnly = isWeekend(date) || (past && !selectablePast);
          const isBlocked = blockedByDay.has(dayKey);
          const disabled = readOnly || isBlocked || dayKey === source?.getTime();

          const cell = occupancy.get(dayKey);
          //TEAMB-3480 the day the lesson sits on outranks the today treatment, so it keeps the
          //green ring until a preview rehomes it and the cell empties
          const isHome = !!cell && !!source && dayKey === key(source);
          const pastTitle = past ? pastByDay.get(dayKey) : undefined;
          const blockedTitle = isBlocked ? blockedByDay.get(dayKey) : undefined;
          const tagSource = cell?.title ?? pastTitle ?? blockedTitle;
          const tag = tagSource ? lessonTag(tagSource) : '';
          const isSelected = !!target && dayKey === key(target);
          const isHovered = !!hover && dayKey === key(hover);
          const isToday = dayKey === today.getTime();
          //a canceled ticket leaves the day pickable, so it only shows once nothing else claims
          //the tag slot — a preview landing here means the day is being reused and takes over
          const isCanceled = !cell && !pastTitle && !blockedTitle && canceledDays.has(dayKey);
          //the grid is keyboard operable, so why a day is occupied or unpickable belongs in the
          //accessible name too — aria-label wins over title, it can't be left out of it
          const note =
            cell?.title ??
            (pastTitle
              ? `${pastTitle} — already done`
              : isCanceled
              ? 'Canceled exit ticket — this day is free'
              : isBlocked
              ? blockedTitle
                ? `${blockedTitle} — already has an exit ticket`
                : 'Already has an exit ticket'
              : isToday
              ? 'Today — where it sits now'
              : undefined);

          return (
            <button
              key={dayKey}
              type="button"
              aria-disabled={disabled}
              tabIndex={disabled ? -1 : 0}
              aria-label={note ? `${shortDay(date)} — ${note}` : shortDay(date)}
              title={note}
              style={date.getDate() === 1 ? { gridColumnStart: date.getDay() + 1 } : undefined}
              onMouseDown={event => disabled && event.preventDefault()}
              onMouseEnter={() => setHover(disabled ? null : date)}
              onFocus={() => setHover(disabled ? null : date)}
              onBlur={() => setHover(null)}
              onClick={() => !disabled && onPick(date)}
              className={`${exitSlipDayShape} tw-h-9 tw-p-0 ${
                disabled
                  ? 'tw-cursor-default'
                  : 'tw-cursor-pointer focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-indigo-600 active:tw-bg-white active:tw-shadow-[inset_0px_0px_0px_3px_#1259FB]'
              } ${
                readOnly
                  ? exitSlipDayBox.empty
                  : isSelected
                  ? exitSlipDayBox.selected
                  : isHovered
                  ? exitSlipDayBox.hovered
                  : cell?.moved
                  ? exitSlipDayBox.moved
                  : isHome
                  ? exitSlipDayHome
                  : isToday
                  ? exitSlipDayBox.today
                  : exitSlipDayBox.empty
              }`}
            >
              <span
                className={`${exitSlipDateText.size} ${
                  isSelected
                    ? exitSlipDateText.selected
                    : cell?.moved
                    ? exitSlipDateText.moved
                    : isToday
                    ? exitSlipDateText.default
                    : disabled
                    ? exitSlipDateText.muted
                    : exitSlipDateText.default
                }`}
              >
                {date.getDate()}
              </span>
              {isCanceled ? (
                <span
                  className={`${exitSlipTagSlot} ${isSelected ? exitSlipTagText.selected : exitSlipTagText.canceled}`}
                >
                  <span className={exitSlipCanceledDot} />
                </span>
              ) : (
                <TagLabel
                  text={cell?.moved && tag ? `→${tag}` : tag || (isToday ? 'Today' : '')}
                  color={
                    isSelected
                      ? exitSlipTagText.selected
                      : (pastTitle || blockedTitle) && !cell && !isToday
                      ? exitSlipTagText.past
                      : isToday && (disabled || !cell)
                      ? exitSlipTagText.todayLabel
                      : exitSlipTagText.default
                  }
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TagLabel: FC<{ text: string; color: string }> = ({ text, color }) => (
  <span className={`${exitSlipTagSlot} ${exitSlipTagText.size} ${color}`}>{text}</span>
);

export default ExitSlipCalendar;
